import {
  faBars,
  faCode,
  faFileLines,
  faImage,
  faRectangle,
  faVideo,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import type { AddElementBlockType, PageBlock } from '@/types/block';

export type { AddElementBlockType } from '@/types/block';

export interface AddElementBlockDefinition {
  blockType: AddElementBlockType;
  label: string;
  icon: IconDefinition;
  createDefaultProps: () => Record<string, unknown>;
}

export const ADD_ELEMENT_BLOCKS: AddElementBlockDefinition[] = [
  {
    blockType: 'text',
    label: 'Text',
    icon: faBars,
    createDefaultProps: () => ({
      content: [
        '<h2>This is a Rich Text Section</h2>',
        '<p>Start typing your content here. This section supports full rich text editing with formatting options, headings, lists, and more.</p>',
        '<p>Replace this placeholder content with your own text to get started.</p>',
      ].join(''),
    }),
  },
  {
    blockType: 'image',
    label: 'Image',
    icon: faImage,
    createDefaultProps: () => ({
      image: {
        url: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1600&q=80&fit=crop',
        alt: 'Dummy Image',
        isInitialProp: true,
      },
      fit: 'cover',
      aspect: 'auto',
    }),
  },
  {
    blockType: 'video',
    label: 'Video',
    icon: faVideo,
    createDefaultProps: () => ({
      video: {
        url: 'https://videos.pexels.com/video-files/34476232/14608809_1280_720_60fps.mp4',
        isInitialProp: true,
      },
      controls: true,
      autoplay: false,
      loop: false,
      muted: false,
      aspect: 'wide',
    }),
  },
  {
    blockType: 'button',
    label: 'Button',
    icon: faRectangle,
    createDefaultProps: () => ({
      text: 'Button',
      style: 'primary',
      linkType: 'external',
    }),
  },
  {
    blockType: 'embed',
    label: 'Embed',
    icon: faCode,
    createDefaultProps: () => ({
      url: '',
      html: '',
    }),
  },
  {
    blockType: 'form',
    label: 'Form',
    icon: faFileLines,
    createDefaultProps: () => ({
      submitLabel: 'Submit',
      background: 'white',
    }),
  },
];

const ADD_ELEMENT_BLOCKS_BY_TYPE = new Map<AddElementBlockType, AddElementBlockDefinition>(
  ADD_ELEMENT_BLOCKS.map((definition) => [definition.blockType, definition]),
);

export function getNextSiblingPosition(
  existingBlocks: PageBlock[] | undefined,
  parentBlockId: string | null,
): number {
  if (!Array.isArray(existingBlocks) || existingBlocks.length === 0) {
    return 1;
  }

  const highestSiblingPosition = existingBlocks.reduce((maxPosition, block) => {
    const blockParentId = block.parentBlockId ?? null;
    if (blockParentId !== parentBlockId) {
      return maxPosition;
    }

    return Number.isFinite(block.position) ? Math.max(maxPosition, block.position) : maxPosition;
  }, 0);

  return highestSiblingPosition + 1;
}

export function createAddElementBlockInput(
  blockType: AddElementBlockType,
  {
    existingBlocks,
    parentBlockId = null,
  }: {
    existingBlocks?: PageBlock[];
    parentBlockId?: string | null;
  } = {},
): Omit<PageBlock, 'id'> {
  const definition = ADD_ELEMENT_BLOCKS_BY_TYPE.get(blockType);
  if (!definition) {
    throw new Error(`Unknown add-element block type: ${blockType}`);
  }

  return {
    blockType,
    props: definition.createDefaultProps(),
    parentBlockId,
    columnSpan: 12,
    position: getNextSiblingPosition(existingBlocks, parentBlockId),
    visible: true,
  };
}

export function createAddElementRootBlockInput(
  blockType: AddElementBlockType,
  existingBlocks?: PageBlock[],
): Omit<PageBlock, 'id'> {
  return createAddElementBlockInput(blockType, { existingBlocks, parentBlockId: null });
}

export function createAddElementChildBlockInput(
  blockType: AddElementBlockType,
  parentBlockId: string,
  existingBlocks?: PageBlock[],
): Omit<PageBlock, 'id'> {
  return createAddElementBlockInput(blockType, { existingBlocks, parentBlockId });
}
